/* jshint esversion: 6 */
/**
 * CalculatorDirectiveController is a controller class for managing the calculator directive.
 * It handles the initialization, configuration, and interaction of the calculator component.
 */
class CalculatorDirectiveController {
    /**
     * Constructor for CalculatorDirectiveController.
     * @param {Object} $scope - AngularJS scope object.
     * @param {Object} $bridges - Service for bridging various functionalities.
     * @param {Object} $timeout - AngularJS timeout service.
     * @param {Object} $rootScope - AngularJS root scope object.
     */
    constructor($scope, $bridges, $timeout, $rootScope) {
        this.$scope = $scope;
        this.$bridges = $bridges;
        this.$timeout = $timeout;
        this.$rootScope = $rootScope;

        // Initialize logger and other meta setup
        this.$logger = this.$bridges.logger();
        this.$scope._logIdentifier = 'CalculatorDirectiveController';
        this.$logger.logExit(this.$scope, 'initialisation');

        // Bind methods to the controller instance
        this.$scope.onClose = this.onClose.bind(this);
        this.$scope.$watch('showCalc', this.onShowCalcChange.bind(this));

        // Load Desmos calculator and initialize
        this.loadDesmosCalc();
        this.tryInit(true);

        // Initialize drag state
        this.$scope.isDragging = false;
        this.$scope.position = { left: '60px', top: '60px' };
        
        // Bind drag handlers
        this.initDraggable();

        // Configuration for Desmos calculator
        this.desmosConfig = {
            functionDefinition: false,
            qwertyKeyboard: false,
            decimalToFraction: false,
            brailleExpressionDownload: false,
            typingAsteriskWritesTimesSymbol: true,
            replaceCommaWith10Exp: true,
            replaceRoundWithReciprocal: true,
            degreeMode: true,
            capExpressionSize: true,
            settingsMenu: false
        };
    }

    initDraggable() {
        this.$timeout(() => {
            const draggable = document.querySelector('.calculator-directive');
            if (!draggable) return;

            let isMouseDown = false;
            let initX, initY;
            const height = draggable.offsetHeight;
            const width = draggable.offsetWidth;

            const onMouseDown = (e) => {
                if (!e.target.closest('.modal-header')) return;
                
                isMouseDown = true;
                document.body.classList.add('no-select');
                
                const rect = draggable.getBoundingClientRect();
                initX = e.clientX - rect.left;
                initY = e.clientY - rect.top;
            };

            const onMouseMove = (e) => {
                if (!isMouseDown) return;

                e.preventDefault();
                
                let cx = e.clientX - initX;
                let cy = e.clientY - initY;

                // Constrain to viewport
                cx = Math.max(0, Math.min(cx, window.innerWidth - width));
                cy = Math.max(0, Math.min(cy, window.innerHeight - height));

                // Update position using $timeout to avoid digest issues
                this.$timeout(() => {
                    this.$scope.position = {
                        left: `${cx}px`,
                        top: `${cy}px`
                    };
                });
            };

            const onMouseUp = () => {
                isMouseDown = false;
                document.body.classList.remove('no-select');
            };

            // Add event listeners
            draggable.addEventListener('mousedown', onMouseDown);
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);

            // Clean up on scope destroy
            this.$scope.$on('$destroy', () => {
                draggable.removeEventListener('mousedown', onMouseDown);
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
            });
        });
    }

    /**
     * Emits an event to toggle the calculator visibility off.
     */
    onClose() {
        this.$rootScope.$emit('toggleCalculator', false);
    }

    /**
     * Handles changes in the calculator visibility.
     * @param {boolean} newVal - New value indicating whether the calculator should be shown.
     */
    onShowCalcChange(newVal) {
        if (this.isLoaded() && newVal) {
            var div = document.getElementsByTagName('calculator')[0].getElementsByTagName('div')[0];
            var zoom = document.body.style.zoom ? document.body.style.zoom : null;
            div.style.zoom = 1 / zoom;
            div.style.transform = 'scale(' + document.body.style.zoom + ')';
            div.style.transformOrigin = '0 0';
            setTimeout(() => {
                window.desmosCalculator.controller.pressKeyInFocusedMathquill("Tab", 0);
            }, 400);
        }
    }

    /**
     * Checks if the Desmos library is loaded.
     * @returns {boolean} - True if Desmos is loaded, otherwise false.
     */
    isLoaded() {
        return typeof Desmos === 'object' && typeof (Desmos.GraphingCalculator) === 'function';
    }

    /**
     * Loads the Desmos calculator script if it is not already loaded.
     */
    loadDesmosCalc() {
        if (this.isLoaded()) {
            return;
        }

        var msg = "Loading Desmos calculator JS";
        this.$logger.info(this.$scope, msg);
        var headRef = document.getElementsByTagName("head")[0];

        var scriptRef = document.createElement('script');
        scriptRef.setAttribute("type", "text/javascript");
        scriptRef.setAttribute("src", "/thirdparty/desmos/calculator.js");

        headRef.appendChild(scriptRef);
    }

    /**
     * Initializes the Desmos calculator with the specified configuration.
     * @returns {boolean} - True if initialization was successful, otherwise false.
     */
    show() {
        this.$logger.info(this.$scope, "Initializing Desmos calc");

        if (this.isLoaded()) {
            Desmos.enabledFeatures = {
                GraphingCalculator: false,
                FourFunctionCalculator: false,
                ScientificCalculator: true
            };

            var elt = document.getElementById('calculatorDiv');
            window.desmosCalculator = Desmos.ScientificCalculator(elt, this.desmosConfig);
            setTimeout(() => {
                window.desmosCalculator.controller.pressKeyInFocusedMathquill("Tab", 0);
            }, 100);

            elt.addEventListener('copy', (ev) => {
                if (window.desmosCalculator.controller.model.getFocusedExpression().latex.length > 0) {
                    var selection = document.getSelection();
                    if (selection.toString().length === 0) {
                        this.$rootScope.copyTexString = window.desmosCalculator.controller.model.getFocusedExpression().latex;
                        ev.clipboardData.setData('text/plain', this.$rootScope.copyTexString);
                        ev.preventDefault();
                    } else {
                        this.$rootScope.copyTexString = selection.toString();
                    }
                }
            });

            this.$logger.info(this.$scope, "Initialized Desmos calc");

            this.initialized = true;
            this.setExpressionSize();

            return true;
        } else {
            this.$logger.info(this.$scope, "Seems like Desmos calc is not yet loaded, will retry");
            return false;
        }
    }

    /**
     * Attempts to initialize the calculator, with retries if necessary.
     * @param {boolean} isFirstTime - Indicates if this is the first initialization attempt.
     */
    tryInit(isFirstTime) {
        this.initAttempts = this.initAttempts || 0;

        if (!angular.isDefined(isFirstTime)) {
            isFirstTime = false;
        }
        if (isFirstTime || !this.show()) {
            if (this.initAttempts > 1000) {
                this.$logger.warn(this.$scope, "Giving up initializing calculator");
                return;
            }
            if (this.initAttempts === 5) {
                this.delay = 500;
            }
            if (!isFirstTime) {
                this.$logger.info(this.$scope, "Will retry to init TI30 calc again in 100ms");
            }
            this.$timeout(() => this.tryInit(), 100);
        }
    }

    /**
     * Sets the expression size limits for the calculator.
     */
    setExpressionSize() {
        window.desmosCalculator.observeEvent('change', () => {
            const mathContainers = document.querySelectorAll('.dcg-exp-mathquill-container');
            mathContainers.forEach(() => {
                const focusedFields = document.getElementsByClassName('dcg-math-field dcg-focus dcg-mq-editable-field dcg-mq-math-mode');
                if (focusedFields[0]) {
                    const textareas = focusedFields[0].getElementsByTagName('textarea');
                    const cursorBlocks = document.querySelectorAll(".dcg-mq-root-block.dcg-mq-hasCursor");

                    if (textareas[0] && cursorBlocks[0]) {
                        textareas[0].addEventListener('keypress', (e) => {
                            if (cursorBlocks[0].getElementsByTagName('*').length > 60) {
                                e.preventDefault();
                                return false;
                            }
                        });
                    }
                }
            });
        });
    }
}

CalculatorDirectiveController.$inject = ['$scope', '$bridges', '$timeout', '$rootScope'];

export default CalculatorDirectiveController;